/**
 * TestingEventCards — Inline card renderers for testing session events.
 *
 * Each card type provides a compact, informative view that renders
 * inside MessageBubble for testing events streamed via SSE.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  TextField,
  Collapse,
  LinearProgress,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as PassedIcon,
  Error as FailedIcon,
  RadioButtonUnchecked as PendingIcon,
  PlayCircle as RunningIcon,
  ThumbUp as ApproveIcon,
  ThumbDown as RejectIcon,
  Healing as HealIcon,
  VpnKey as CredentialIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Security as SecurityIcon,
  Api as ApiIcon,
  Warning as WarningIcon,
  Lock as LockIcon,
  BugReport as BugReportIcon,
  SmartToy as SmartToyIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutline as ErrorOutlineIcon,
  Speed as SpeedIcon,
  Code as CodeIcon,
  Replay as RetryIcon,
  SkipNext as SkipIcon,
  Timer as TimerIcon,
  StopCircle as StopIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import { TestingEventCard as TestingEventCardType, GeneratedCodePayload } from '../types';
import testingService from '../services/testing.service';
import GeneratedCodeViewer from './GeneratedCodeViewer';
import APITestingKnowledgeCard from './APITestingKnowledgeCard';

// ============================================================================
// Phase Change Card
// ============================================================================

const PHASE_COLORS: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  initializing: 'default',
  loading_prior_session: 'info',
  planning: 'info',
  provisioning: 'info',
  awaiting_credentials: 'warning',
  awaiting_captcha: 'warning',
  executing: 'primary',
  generating_steps: 'info',
  awaiting_approval: 'warning',
  completed: 'success',
  failed: 'error',
  cancelled: 'default',
  generating_code: 'info',
  creating_tms_entities: 'info',
  interactive: 'success',
  review_failures: 'warning',
  crawling: 'info',
  exploring: 'info',
  analyzing: 'info',
  page_testing: 'primary',
  page_code_generation: 'info',
  page_execution: 'primary',
};

const PhaseChangeCard: React.FC<{ data: Record<string, any> }> = ({ data }) => (
  <Chip
    label={String(data.phase || '').replace(/_/g, ' ')}
    size="small"
    color={PHASE_COLORS[data.phase] || 'default'}
    variant="filled"
    sx={{ fontWeight: 600, textTransform: 'capitalize', my: 0.5 }}
  />
);

// ============================================================================
// Steps Generated Card
// ============================================================================

const StepsCard: React.FC<{ data: Record<string, any>; sessionId?: string }> = ({ data, sessionId }) => {
  const [expanded, setExpanded] = useState(false);
  const [acted, setActed] = useState(false);
  const steps: any[] = data.steps || [];

  const handleApprove = () => {
    if (sessionId) {
      testingService.approveSteps(sessionId).catch(console.error);
      setActed(true);
    }
  };

  const handleReject = () => {
    if (sessionId) {
      testingService.rejectSteps(sessionId).catch(console.error);
      setActed(true);
    }
  };

  return (
    <Box sx={{ border: '1px solid', borderColor: 'info.200', borderRadius: 1.5, overflow: 'hidden', my: 0.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: 'info.50',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
          {steps.length} test step{steps.length !== 1 ? 's' : ''} generated
        </Typography>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <List dense disablePadding sx={{ px: 1 }}>
          {steps.map((step: any, idx: number) => (
            <ListItem key={idx} sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <PendingIcon fontSize="small" color="disabled" />
              </ListItemIcon>
              <ListItemText
                primary={step.description || step.action_id}
                primaryTypographyProps={{ variant: 'body2', fontSize: '0.85rem' }}
              />
            </ListItem>
          ))}
        </List>
      </Collapse>
      {!acted && (
        <Box sx={{ display: 'flex', gap: 1, px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button size="small" variant="contained" color="success" startIcon={<ApproveIcon />} onClick={handleApprove}>
            Approve
          </Button>
          <Button size="small" variant="outlined" color="error" startIcon={<RejectIcon />} onClick={handleReject}>
            Reject
          </Button>
        </Box>
      )}
      {acted && (
        <Box sx={{ px: 1.5, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">Action submitted</Typography>
        </Box>
      )}
    </Box>
  );
};

// ============================================================================
// Step Progress Card (executing / completed)
// ============================================================================

const StepProgressCard: React.FC<{ data: Record<string, any>; type: 'step_executing' | 'step_completed' }> = ({ data, type }) => {
  const isExecuting = type === 'step_executing';
  const isPassed = data.status === 'passed';
  const stepNum = (data.step_index ?? 0) + 1;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
      {isExecuting ? (
        <RunningIcon fontSize="small" color="primary" />
      ) : isPassed ? (
        <PassedIcon fontSize="small" color="success" />
      ) : (
        <FailedIcon fontSize="small" color="error" />
      )}
      <Typography variant="body2" fontSize="0.85rem" sx={{ flex: 1 }}>
        Step {stepNum}{data.description ? `: ${data.description}` : ''}
      </Typography>
      {data.duration_ms && (
        <Typography variant="caption" color="text.secondary">
          {data.duration_ms}ms
        </Typography>
      )}
      {data.healed && (
        <Chip label="Healed" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
      )}
    </Box>
  );
};

// ============================================================================
// Healing Card
// ============================================================================

const HealingCard: React.FC<{ data: Record<string, any> }> = ({ data }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1, bgcolor: 'warning.50', borderRadius: 1, my: 0.5 }}>
    <HealIcon fontSize="small" color="warning" sx={{ mt: 0.25 }} />
    <Box sx={{ flex: 1 }}>
      <Typography variant="body2" fontWeight={600} fontSize="0.85rem">
        Self-healed step {(data.step_index ?? 0) + 1}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ fontFamily: 'monospace' }}>
        {data.old_selector} → {data.new_selector}
      </Typography>
      {data.strategy && (
        <Typography variant="caption" color="text.secondary">
          Strategy: {data.strategy}
        </Typography>
      )}
    </Box>
  </Box>
);

// ============================================================================
// Credential Request Card
// ============================================================================

const CredentialRequestCard: React.FC<{ data: Record<string, any>; sessionId?: string }> = ({ data, sessionId }) => {
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  // Determine field layout from backend payload; fall back to username+password
  const fields: Array<{ type: string; label?: string }> = Array.isArray(data.fields) && data.fields.length > 0
    ? data.fields
    : [{ type: 'username', label: 'Username / Email' }, { type: 'password', label: 'Password' }];

  const isOtpMode = fields.length === 1 && fields[0].type === 'otp';
  const isPhoneMode = fields.length === 1 && fields[0].type === 'phone';

  const updateField = (type: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [type]: value }));
  };

  const handleSubmit = () => {
    if (!sessionId) return;
    // Backend API accepts { username, password, extra_fields }.
    // For phone / OTP flows the backend reads from "username" as fallback.
    const primaryValue = fieldValues[fields[0].type] || '';
    if (!primaryValue) return;

    const username = fieldValues['username'] || fieldValues['email'] || fieldValues['phone'] || fieldValues['otp'] || primaryValue;
    const password = fieldValues['password'] || '';
    testingService.submitCredentials(sessionId, username, password).catch(console.error);
    setSubmitted(true);
  };

  const handleSkip = () => {
    if (sessionId) {
      testingService.skipLogin(sessionId).catch(console.error);
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
        <CredentialIcon fontSize="small" color="action" />
        <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
          {isOtpMode ? 'OTP submitted' : isPhoneMode ? 'Phone number submitted' : 'Credentials submitted'}
        </Typography>
      </Box>
    );
  }

  const fieldLabel = (f: { type: string; label?: string }) => {
    if (f.label) return f.label;
    switch (f.type) {
      case 'otp': return 'OTP Code';
      case 'phone': return 'Phone Number';
      case 'email': return 'Email';
      case 'username': return 'Username / Email';
      case 'password': return 'Password';
      default: return f.type;
    }
  };

  const headerText = isOtpMode
    ? 'Enter verification code'
    : isPhoneMode
      ? 'Phone number required'
      : 'Login credentials required';

  return (
    <Box sx={{ border: '1px solid', borderColor: 'warning.300', borderRadius: 1.5, p: 1.5, my: 0.5, bgcolor: 'warning.50' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <CredentialIcon fontSize="small" color="warning" />
        <Typography variant="body2" fontWeight={600}>
          {headerText}
        </Typography>
      </Box>
      {data.message && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {data.message}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        {fields.map((f) => (
          <TextField
            key={f.type}
            size="small"
            label={fieldLabel(f)}
            type={f.type === 'password' ? 'password' : 'text'}
            value={fieldValues[f.type] || ''}
            onChange={(e) => updateField(f.type, e.target.value)}
            autoFocus={fields.length === 1}
            inputProps={f.type === 'otp' ? { maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' } : undefined}
            sx={{ flex: 1 }}
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          size="small"
          variant="contained"
          onClick={handleSubmit}
          disabled={!(fieldValues[fields[0].type] || '')}
        >
          Submit
        </Button>
        <Button size="small" variant="outlined" color="inherit" onClick={handleSkip}>
          Skip Login
        </Button>
      </Box>
    </Box>
  );
};

// ============================================================================
// Login Status Card
// ============================================================================

const LoginStatusCard: React.FC<{ data: Record<string, any> }> = ({ data }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
    {data.success ? (
      <CheckCircleOutlineIcon fontSize="small" color="success" />
    ) : (
      <ErrorOutlineIcon fontSize="small" color="error" />
    )}
    <Typography variant="body2" fontSize="0.85rem">
      {data.success ? 'Login successful' : `Login failed${data.error ? `: ${data.error}` : ''}`}
    </Typography>
  </Box>
);

// ============================================================================
// Captcha Card
// ============================================================================

const CaptchaCard: React.FC<{ data: Record<string, any>; sessionId?: string }> = ({ data, sessionId }) => {
  const [acted, setActed] = useState(false);

  const handleSolve = () => {
    if (sessionId) {
      testingService.solveCaptcha(sessionId).catch(console.error);
      setActed(true);
    }
  };

  const handleSkip = () => {
    if (sessionId) {
      testingService.skipCaptcha(sessionId).catch(console.error);
      setActed(true);
    }
  };

  return (
    <Box sx={{ border: '1px solid', borderColor: 'warning.300', borderRadius: 1.5, p: 1.5, my: 0.5, bgcolor: 'warning.50' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <WarningIcon fontSize="small" color="warning" />
        <Typography variant="body2" fontWeight={600}>
          CAPTCHA detected
        </Typography>
      </Box>
      {data.url && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          on {data.url}
        </Typography>
      )}
      {!acted ? (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="contained" onClick={handleSolve}>
            Solve CAPTCHA
          </Button>
          <Button size="small" variant="outlined" color="inherit" onClick={handleSkip}>
            Skip
          </Button>
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary">Action submitted</Typography>
      )}
    </Box>
  );
};

// ============================================================================
// Approval Card (generic step approval)
// ============================================================================

const ApprovalCard: React.FC<{ data: Record<string, any>; sessionId?: string }> = ({ data, sessionId }) => {
  const [acted, setActed] = useState(false);

  const handleApprove = () => {
    if (sessionId) {
      testingService.approveSteps(sessionId).catch(console.error);
      setActed(true);
    }
  };

  const handleReject = () => {
    if (sessionId) {
      testingService.rejectSteps(sessionId).catch(console.error);
      setActed(true);
    }
  };

  return (
    <Box sx={{ border: '1px solid', borderColor: 'warning.300', borderRadius: 1.5, p: 1.5, my: 0.5 }}>
      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
        {data.message || 'Approval required'}
      </Typography>
      {!acted ? (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="contained" color="success" startIcon={<ApproveIcon />} onClick={handleApprove}>
            Approve
          </Button>
          <Button size="small" variant="outlined" color="error" startIcon={<RejectIcon />} onClick={handleReject}>
            Reject
          </Button>
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary">Action submitted</Typography>
      )}
    </Box>
  );
};

// ============================================================================
// Review Failures Card
// ============================================================================

const ReviewFailuresCard: React.FC<{ data: Record<string, any>; sessionId?: string }> = ({ data, sessionId }) => {
  const [expanded, setExpanded] = useState(false);
  const [acted, setActed] = useState(false);
  const failedSteps: any[] = data.failed_steps || [];

  const handleRetry = () => {
    if (sessionId) {
      testingService.approveReexecution(sessionId).catch(console.error);
      setActed(true);
    }
  };

  const handleSkip = () => {
    if (sessionId) {
      testingService.skipReexecution(sessionId).catch(console.error);
      setActed(true);
    }
  };

  return (
    <Box sx={{ border: '1px solid', borderColor: 'error.200', borderRadius: 1.5, overflow: 'hidden', my: 0.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: 'error.50',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <FailedIcon fontSize="small" color="error" />
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
          {data.failed_count || failedSteps.length} failed step{(data.failed_count || failedSteps.length) !== 1 ? 's' : ''}
          {data.passed_count ? ` (${data.passed_count} passed)` : ''}
        </Typography>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <List dense disablePadding sx={{ px: 1 }}>
          {failedSteps.map((step: any, idx: number) => (
            <ListItem key={idx} sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <FailedIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText
                primary={step.description || step.action_id || `Step ${step.step_index + 1}`}
                secondary={step.error}
                primaryTypographyProps={{ variant: 'body2', fontSize: '0.85rem' }}
                secondaryTypographyProps={{ variant: 'caption', color: 'error' }}
              />
            </ListItem>
          ))}
        </List>
      </Collapse>
      {!acted ? (
        <Box sx={{ display: 'flex', gap: 1, px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button size="small" variant="contained" color="warning" startIcon={<RetryIcon />} onClick={handleRetry}>
            Re-execute
          </Button>
          <Button size="small" variant="outlined" color="inherit" startIcon={<SkipIcon />} onClick={handleSkip}>
            Skip
          </Button>
        </Box>
      ) : (
        <Box sx={{ px: 1.5, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">Action submitted</Typography>
        </Box>
      )}
    </Box>
  );
};

// ============================================================================
// Quality Assessment Card
// ============================================================================

const QualityAssessmentCard: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const gradeColor = data.grade === 'A' ? 'success' : data.grade === 'B' ? 'info' : data.grade === 'C' ? 'warning' : 'error';

  return (
    <Box sx={{ border: '1px solid', borderColor: `${gradeColor}.200`, borderRadius: 1.5, p: 1.5, my: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <SpeedIcon fontSize="small" color={gradeColor as any} />
        <Typography variant="body2" fontWeight={600}>Quality Assessment</Typography>
        <Chip label={`Grade: ${data.grade}`} size="small" color={gradeColor as any} sx={{ fontWeight: 700 }} />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="caption">
          Pass rate: <strong>{data.pass_rate}%</strong>
        </Typography>
        <Typography variant="caption">
          Total: <strong>{data.total_cases}</strong>
        </Typography>
        {data.flaky_count > 0 && (
          <Typography variant="caption" color="warning.main">
            Flaky: <strong>{data.flaky_count}</strong>
          </Typography>
        )}
        {data.flakiness_score != null && (
          <Typography variant="caption">
            Flakiness: <strong>{data.flakiness_score}%</strong>
          </Typography>
        )}
      </Box>
      {data.summary && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {data.summary}
        </Typography>
      )}
    </Box>
  );
};

// ============================================================================
// Code Generated Card
// ============================================================================

const CodeGeneratedCard: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const [expanded, setExpanded] = useState(false);

  if (!data.files || data.files.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
        <CodeIcon fontSize="small" color="info" />
        <Typography variant="body2" fontSize="0.85rem">Test code generated</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ my: 0.5 }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', py: 0.5 }}
        onClick={() => setExpanded(!expanded)}
      >
        <CodeIcon fontSize="small" color="info" />
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
          {data.files.length} file{data.files.length !== 1 ? 's' : ''} generated
        </Typography>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ mt: 0.5 }}>
          <GeneratedCodeViewer code={data as unknown as GeneratedCodePayload} />
        </Box>
      </Collapse>
    </Box>
  );
};

// ============================================================================
// Session Complete Card
// ============================================================================

const SessionCompleteCard: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const summary = data.summary || {};

  return (
    <Box sx={{ border: '1px solid', borderColor: 'success.200', borderRadius: 1.5, p: 1.5, my: 0.5, bgcolor: 'success.50' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <CheckCircleOutlineIcon fontSize="small" color="success" />
        <Typography variant="body2" fontWeight={600}>Session Complete</Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {summary.total_steps != null && (
          <Typography variant="caption">
            Steps: <strong>{summary.total_steps}</strong>
          </Typography>
        )}
        {summary.passed != null && (
          <Typography variant="caption" color="success.main">
            Passed: <strong>{summary.passed}</strong>
          </Typography>
        )}
        {summary.failed != null && summary.failed > 0 && (
          <Typography variant="caption" color="error.main">
            Failed: <strong>{summary.failed}</strong>
          </Typography>
        )}
      </Box>
      {summary.video_url && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
          <a href={summary.video_url} target="_blank" rel="noopener noreferrer">
            View recording
          </a>
        </Typography>
      )}
    </Box>
  );
};

// ============================================================================
// Session Error Card
// ============================================================================

const SessionErrorCard: React.FC<{ data: Record<string, any> }> = ({ data }) => (
  <Alert
    severity={data.recoverable ? 'warning' : 'error'}
    sx={{ my: 0.5, py: 0.25, '& .MuiAlert-message': { fontSize: '0.85rem' } }}
  >
    {data.message || 'An error occurred'}
  </Alert>
);

// ============================================================================
// OTP Status Card
// ============================================================================

const OtpStatusCard: React.FC<{ data: Record<string, any> }> = ({ data }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
    <SmartToyIcon fontSize="small" color="info" />
    <Typography variant="body2" fontSize="0.85rem" color="text.secondary">
      {data.message || 'OTP processing...'}
    </Typography>
  </Box>
);

// ============================================================================
// DOM Analysis Card
// ============================================================================

const DomAnalysisCard: React.FC<{ data: Record<string, any> }> = ({ data }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.25 }}>
    <Typography variant="caption" color="text.secondary">
      Page analysis: {data.elements_count || 0} elements, {data.forms_count || 0} forms, {data.links_count || 0} links
    </Typography>
  </Box>
);

// ============================================================================
// API Discovery Card
// ============================================================================

const APIDiscoveryCard: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  const endpoints: any[] = data.endpoints || [];

  return (
    <Box sx={{ border: '1px solid', borderColor: 'info.200', borderRadius: 1.5, overflow: 'hidden', my: 0.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: 'info.50',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <ApiIcon fontSize="small" color="info" />
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
          {data.endpoints_found || endpoints.length} API endpoint{(data.endpoints_found || endpoints.length) !== 1 ? 's' : ''} discovered
        </Typography>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <List dense disablePadding sx={{ px: 1, maxHeight: 200, overflow: 'auto' }}>
          {endpoints.slice(0, 20).map((ep: any, idx: number) => (
            <ListItem key={idx} sx={{ py: 0.25 }}>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip
                      label={ep.method}
                      size="small"
                      color={ep.method === 'GET' ? 'success' : ep.method === 'POST' ? 'primary' : ep.method === 'DELETE' ? 'error' : 'default'}
                      sx={{ fontWeight: 600, fontSize: '0.65rem', height: 18, minWidth: 40 }}
                    />
                    <Typography variant="body2" fontSize="0.8rem" fontFamily="monospace">
                      {ep.path}
                    </Typography>
                  </Box>
                }
                secondary={ep.description}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          ))}
        </List>
      </Collapse>
    </Box>
  );
};

// ============================================================================
// API Config Request Card
// ============================================================================

const MethodChip: React.FC<{ method: string }> = ({ method }) => {
  const colors: Record<string, string> = {
    GET: '#2e7d32', POST: '#1565c0', PUT: '#e65100', PATCH: '#6a1b9a', DELETE: '#c62828',
  };
  return (
    <Typography component="span" sx={{
      fontSize: '0.65rem', fontWeight: 700, color: '#fff', px: 0.5, py: 0.1,
      borderRadius: 0.5, bgcolor: colors[method] || '#757575', fontFamily: 'monospace',
    }}>
      {method}
    </Typography>
  );
};

const APIConfigCard: React.FC<{ data: Record<string, any>; sessionId?: string }> = ({ data, sessionId }) => {
  const [authType, setAuthType] = useState<'email' | 'phone'>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [authEndpoint, setAuthEndpoint] = useState('');
  const [tokenPath, setTokenPath] = useState('access_token');
  const [maxTests, setMaxTests] = useState(50);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [docsUrl, setDocsUrl] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [showCoverage, setShowCoverage] = useState(false);

  const authEndpoints: any[] = data.auth_endpoints || [];
  const endpointGroups: any[] = data.endpoint_groups || [];
  const coveragePlan: any = data.coverage_plan || null;

  const handleToggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleSubmit = () => {
    if (!sessionId) return;
    const username = authType === 'phone' ? phone : email;
    testingService.submitCredentials(sessionId, username, password, {
      auth_type: authType,
      auth_phone: authType === 'phone' ? phone : '',
      auth_endpoint: authEndpoint,
      token_path: tokenPath,
      selected_tags: JSON.stringify(selectedTags),
      max_tests: String(maxTests),
      docs_url: docsUrl,
    });
    setSubmitted(true);
  };

  const handleSkip = () => {
    if (sessionId) {
      testingService.skipLogin(sessionId);
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
        <ApiIcon fontSize="small" color="info" />
        <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
          {(email || phone) ? `API testing configured (${authType === 'phone' ? 'Phone/OTP' : 'Email/Password'})` : 'API testing configured (no auth)'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ border: '1px solid', borderColor: 'info.300', borderRadius: 1.5, overflow: 'hidden', my: 0.5 }}>
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          px: 1.5, py: 1, bgcolor: 'info.50', cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <ApiIcon fontSize="small" color="info" />
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
          Configure API Testing ({data.total_endpoints || 0} endpoints discovered)
        </Typography>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ px: 1.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {data.message && (
            <Typography variant="caption" color="text.secondary">{data.message}</Typography>
          )}

          {/* Auth type selector */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" fontWeight={600} fontSize="0.85rem">Authentication</Typography>
            <Box sx={{ display: 'flex', ml: 'auto', gap: 0.5 }}>
              <Chip label="Email / Password" size="small"
                color={authType === 'email' ? 'primary' : 'default'}
                variant={authType === 'email' ? 'filled' : 'outlined'}
                onClick={() => setAuthType('email')} sx={{ cursor: 'pointer' }} />
              <Chip label="Phone / OTP" size="small"
                color={authType === 'phone' ? 'primary' : 'default'}
                variant={authType === 'phone' ? 'filled' : 'outlined'}
                onClick={() => setAuthType('phone')} sx={{ cursor: 'pointer' }} />
            </Box>
          </Box>

          {authType === 'email' ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField size="small" label="Email / Username" value={email}
                onChange={(e) => setEmail(e.target.value)} sx={{ flex: 1 }} />
              <TextField size="small" label="Password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} sx={{ flex: 1 }} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField size="small" label="Phone Number" value={phone}
                onChange={(e) => setPhone(e.target.value)} fullWidth
                placeholder="+91 9876543210"
                helperText="OTP will be sent to this number during auth step — you'll be prompted to enter it" />
            </Box>
          )}

          {authEndpoints.length > 0 && (
            <TextField size="small" select label="Auth Endpoint" value={authEndpoint}
              onChange={(e) => setAuthEndpoint(e.target.value)} fullWidth
              SelectProps={{ native: true }}
            >
              <option value="">Auto-detect</option>
              {authEndpoints.map((ep: any, i: number) => (
                <option key={i} value={ep.path}>{ep.method} {ep.path}</option>
              ))}
            </TextField>
          )}

          <TextField size="small" label="Token JSON path" value={tokenPath}
            onChange={(e) => setTokenPath(e.target.value)}
            helperText="JSON path to extract token from login response"
            fullWidth />

          {/* Scope — Expandable Endpoint Groups */}
          {endpointGroups.length > 0 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={600} fontSize="0.85rem">
                  Endpoint Groups {selectedTags.length > 0 && `(${selectedTags.length} selected)`}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  Click chip to select, click arrow to expand
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {endpointGroups.map((g: any) => (
                  <Box key={g.name}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip label={`${g.name} (${g.count})`} size="small"
                        color={selectedTags.includes(g.name) ? 'primary' : 'default'}
                        variant={selectedTags.includes(g.name) ? 'filled' : 'outlined'}
                        onClick={() => handleToggleTag(g.name)}
                        sx={{ cursor: 'pointer' }} />
                      {g.endpoints && g.endpoints.length > 0 && (
                        <IconButton size="small"
                          onClick={() => setExpandedGroup(expandedGroup === g.name ? null : g.name)}
                          sx={{ p: 0.25 }}>
                          {expandedGroup === g.name
                            ? <ExpandLessIcon sx={{ fontSize: '1rem' }} />
                            : <ExpandMoreIcon sx={{ fontSize: '1rem' }} />}
                        </IconButton>
                      )}
                    </Box>
                    <Collapse in={expandedGroup === g.name}>
                      {g.endpoints && (
                        <Box sx={{ ml: 1, mt: 0.5, mb: 0.5, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                          {g.endpoints.map((ep: any, i: number) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}>
                              <MethodChip method={ep.method} />
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.primary' }}>
                                {ep.path}
                              </Typography>
                              {ep.auth_required && <LockIcon sx={{ fontSize: '0.7rem', color: 'warning.main' }} />}
                              {ep.description && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }} noWrap>
                                  {ep.description}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Collapse>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {/* Test Coverage Plan */}
          {coveragePlan && (
            <>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', mt: 0.5 }}
                onClick={() => setShowCoverage(!showCoverage)}
              >
                <SecurityIcon sx={{ fontSize: '1rem', color: 'info.main' }} />
                <Typography variant="body2" fontWeight={600} fontSize="0.85rem">
                  Test Coverage Plan
                </Typography>
                <IconButton size="small" sx={{ p: 0.25, ml: 'auto' }}>
                  {showCoverage ? <ExpandLessIcon sx={{ fontSize: '1rem' }} /> : <ExpandMoreIcon sx={{ fontSize: '1rem' }} />}
                </IconButton>
              </Box>
              <Collapse in={showCoverage}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 0.5 }}>
                  {coveragePlan.test_types?.map((tt: any) => {
                    const iconMap: Record<string, React.ReactNode> = {
                      check: <PassedIcon sx={{ fontSize: '1rem', color: 'success.main' }} />,
                      error: <BugReportIcon sx={{ fontSize: '1rem', color: 'error.main' }} />,
                      security: <SecurityIcon sx={{ fontSize: '1rem', color: 'warning.main' }} />,
                      lock: <LockIcon sx={{ fontSize: '1rem', color: 'info.main' }} />,
                      warning: <WarningIcon sx={{ fontSize: '1rem', color: 'warning.main' }} />,
                    };
                    return (
                      <Box key={tt.type} sx={{
                        display: 'flex', alignItems: 'flex-start', gap: 0.75,
                        p: 0.75, borderRadius: 1, bgcolor: 'action.hover',
                      }}>
                        {iconMap[tt.icon] || <PassedIcon sx={{ fontSize: '1rem' }} />}
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" fontWeight={600} fontSize="0.8rem">{tt.type}</Typography>
                            <Chip label={`~${tt.estimated_tests} tests`} size="small"
                              sx={{ height: 18, fontSize: '0.65rem' }} />
                          </Box>
                          <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                            {tt.description}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                  {coveragePlan.method_distribution && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                      <Typography variant="caption" color="text.secondary" fontSize="0.7rem" sx={{ mr: 0.5 }}>
                        Methods:
                      </Typography>
                      {Object.entries(coveragePlan.method_distribution).map(([method, count]) => (
                        <Box key={method} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <MethodChip method={method} />
                          <Typography variant="caption" fontSize="0.7rem">{count as number}</Typography>
                        </Box>
                      ))}
                      {coveragePlan.auth_required_count > 0 && (
                        <>
                          <Typography variant="caption" color="text.secondary" fontSize="0.7rem" sx={{ ml: 0.5 }}>
                            |
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            <LockIcon sx={{ fontSize: '0.7rem', color: 'warning.main' }} />
                            <Typography variant="caption" fontSize="0.7rem">
                              {coveragePlan.auth_required_count} require auth
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              </Collapse>
            </>
          )}

          {/* Settings */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" label="Max tests" type="number" value={maxTests}
              onChange={(e) => setMaxTests(Math.max(10, Math.min(200, Number(e.target.value) || 50)))}
              inputProps={{ min: 10, max: 200 }}
              sx={{ width: 120 }} />
            <TextField size="small" label="API docs URL (optional)" value={docsUrl}
              onChange={(e) => setDocsUrl(e.target.value)} sx={{ flex: 1 }} />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button size="small" variant="contained" color="primary" onClick={handleSubmit}>
            Configure & Generate Plan
          </Button>
          <Button size="small" variant="outlined" color="inherit" onClick={handleSkip}>
            Skip (use defaults)
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
};

// ============================================================================
// API Batch Summary Card
// ============================================================================

const APIBatchSummaryCard: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const batchTotal = (data.batch_passed || 0) + (data.batch_failed || 0);
  const batchRate = batchTotal > 0 ? Math.round(((data.batch_passed || 0) / batchTotal) * 100) : 0;
  const overallTotal = (data.overall_passed || 0) + (data.overall_failed || 0);
  const rateColor = batchRate > 80 ? 'success.main' : batchRate > 50 ? 'warning.main' : 'error.main';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25, flexWrap: 'wrap' }}>
      <Chip
        label={`Batch ${data.batch_number || '?'}/${data.total_batches || '?'}`}
        size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }}
      />
      <Typography variant="body2" fontSize="0.85rem">
        {data.batch_passed || 0} passed, {data.batch_failed || 0} failed
      </Typography>
      <Typography variant="body2" fontSize="0.85rem" fontWeight={600} sx={{ color: rateColor }}>
        ({batchRate}%)
      </Typography>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      <Typography variant="caption" color="text.secondary">
        Overall: {data.overall_passed || 0}/{overallTotal}
      </Typography>
    </Box>
  );
};

// ============================================================================
// API Analysis Card
// ============================================================================

const APIAnalysisCard: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  const [resultsExpanded, setResultsExpanded] = useState(false);
  const categories: any[] = data.categories || [];
  const results: any[] = data.results || [];
  const gradeColor = data.grade === 'A' ? 'success' : data.grade === 'B' ? 'info' : data.grade === 'C' ? 'warning' : 'error';

  return (
    <Box sx={{ border: '1px solid', borderColor: `${gradeColor}.200`, borderRadius: 1.5, overflow: 'hidden', my: 0.5 }}>
      <Box sx={{ px: 1.5, py: 1, bgcolor: `${gradeColor}.50` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <SmartToyIcon fontSize="small" color={gradeColor as any} />
          <Typography variant="body2" fontWeight={600}>API Test Analysis</Typography>
          <Chip label={`Grade: ${data.grade}`} size="small" color={gradeColor as any} sx={{ fontWeight: 700 }} />
          <Typography variant="body2" fontWeight={700} sx={{ color: `${gradeColor}.main`, ml: 'auto' }}>
            {data.pass_rate}%
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="caption">
            Total: <strong>{data.total || 0}</strong>
          </Typography>
          <Typography variant="caption" color="success.main">
            Passed: <strong>{data.passed || 0}</strong>
          </Typography>
          {(data.failed || 0) > 0 && (
            <Typography variant="caption" color="error.main">
              Failed: <strong>{data.failed}</strong>
            </Typography>
          )}
        </Box>
      </Box>

      {data.top_recommendation && (
        <Alert severity="info" sx={{ borderRadius: 0, py: 0.25, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
          {data.top_recommendation}
        </Alert>
      )}

      {categories.length > 0 && (
        <>
          <Box
            sx={{ px: 1.5, py: 0.75, cursor: 'pointer', display: 'flex', alignItems: 'center', borderTop: '1px solid', borderColor: 'divider' }}
            onClick={() => setExpanded(!expanded)}
          >
            <Typography variant="caption" fontWeight={600} sx={{ flex: 1 }}>
              {categories.length} failure categor{categories.length !== 1 ? 'ies' : 'y'}
            </Typography>
            <IconButton size="small">
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Box>
          <Collapse in={expanded}>
            <List dense disablePadding sx={{ px: 1 }}>
              {categories.map((cat: any, idx: number) => (
                <ListItem key={idx} sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Chip
                      label={cat.severity || 'info'}
                      size="small"
                      color={cat.severity === 'critical' ? 'error' : cat.severity === 'high' ? 'error' : cat.severity === 'medium' ? 'warning' : 'default'}
                      sx={{ fontSize: '0.6rem', height: 18, fontWeight: 600, textTransform: 'uppercase' }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${cat.group || 'Unknown'} (${cat.count || 0})`}
                    secondary={
                      <>
                        {cat.root_cause && <span>{cat.root_cause}</span>}
                        {cat.recommendation && <span style={{ display: 'block', fontWeight: 500 }}>Fix: {cat.recommendation}</span>}
                      </>
                    }
                    primaryTypographyProps={{ variant: 'body2', fontSize: '0.85rem', fontWeight: 600 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </>
      )}

      {results.length > 0 && (
        <>
          <Box
            sx={{ px: 1.5, py: 0.75, cursor: 'pointer', display: 'flex', alignItems: 'center', borderTop: '1px solid', borderColor: 'divider' }}
            onClick={() => setResultsExpanded(!resultsExpanded)}
          >
            <Typography variant="caption" fontWeight={600} sx={{ flex: 1 }}>
              All Results ({results.length} steps)
            </Typography>
            <IconButton size="small">
              {resultsExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Box>
          <Collapse in={resultsExpanded}>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              <List dense disablePadding>
                {results.map((r: any, idx: number) => (
                  <ListItem
                    key={idx}
                    sx={{
                      py: 0.25,
                      px: 1.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      bgcolor: r.status === 'failed' ? 'error.50' : undefined,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      {r.status === 'passed'
                        ? <PassedIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        : <FailedIcon sx={{ fontSize: 16, color: 'error.main' }} />
                      }
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 20 }}>
                            {r.step_index + 1}.
                          </Typography>
                          {r.group && (
                            <Chip label={r.group} size="small" sx={{ fontSize: '0.55rem', height: 16, fontWeight: 600 }} />
                          )}
                          <Typography variant="caption" sx={{ flex: 1 }} noWrap>
                            {r.description || r.action_id}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>
                            {r.duration_ms}ms
                          </Typography>
                        </Box>
                      }
                      secondary={r.status === 'failed' && r.error ? (
                        <Typography variant="caption" color="error.main" sx={{ fontSize: '0.7rem', wordBreak: 'break-word' }}>
                          {r.error}
                        </Typography>
                      ) : undefined}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Collapse>
        </>
      )}
    </Box>
  );
};

// ============================================================================
// API Step Result Card
// ============================================================================

const statusCodeReason = (code: number): string => {
  const reasons: Record<number, string> = {
    400: 'Bad Request — invalid input or missing required fields',
    401: 'Unauthorized — missing or invalid auth token',
    403: 'Forbidden — insufficient permissions',
    404: 'Not Found — endpoint or resource does not exist',
    405: 'Method Not Allowed',
    409: 'Conflict — resource already exists',
    422: 'Validation Error — request body failed validation',
    429: 'Rate Limited — too many requests',
    500: 'Internal Server Error',
    502: 'Bad Gateway',
    503: 'Service Unavailable',
  };
  return reasons[code] || (code >= 400 && code < 500 ? 'Client Error' : code >= 500 ? 'Server Error' : '');
};

const APIStepResultCard: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  const statusCode = data.response?.status_code;
  const isSuccess = statusCode && statusCode >= 200 && statusCode < 300;
  const method = data.request?.method || '';
  const url = data.request?.url || '';
  const stepNum = (data.step_index ?? 0) + 1;
  const totalSteps = data.total_steps;
  const description = data.description;
  const category = data.category;
  const group = data.group;
  const error = data.error;
  const responseBody = data.response?.body;
  const requestBody = data.request?.body;
  const requestHeaders = data.request?.headers;

  // Compact summary for passed tests
  if (isSuccess && !expanded) {
    return (
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', gap: 0.75, py: 0.25,
        cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 0.5, px: 0.5,
      }} onClick={() => setExpanded(true)}>
        <PassedIcon sx={{ fontSize: '0.9rem', color: 'success.main', mt: 0.2 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            {totalSteps && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                [{stepNum}/{totalSteps}]
              </Typography>
            )}
            <MethodChip method={method} />
            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }} noWrap>
              {url.replace(/^https?:\/\/[^/]+/, '')}
            </Typography>
            <Chip label={statusCode} size="small" color="success" variant="outlined"
              sx={{ height: 16, fontSize: '0.6rem' }} />
            {data.duration_ms != null && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                {data.duration_ms}ms
              </Typography>
            )}
          </Box>
          {description && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }} noWrap>
              {description}
            </Typography>
          )}
        </Box>
        <ExpandMoreIcon sx={{ fontSize: '0.85rem', color: 'text.disabled', mt: 0.2 }} />
      </Box>
    );
  }

  // Expanded view for failed tests (auto-expand) or clicked passed tests
  return (
    <Box sx={{
      border: '1px solid',
      borderColor: isSuccess ? 'success.200' : 'error.200',
      borderRadius: 1, overflow: 'hidden', my: 0.25,
    }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.5,
          px: 1, py: 0.5,
          bgcolor: isSuccess ? 'success.50' : 'error.50',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        {isSuccess
          ? <PassedIcon sx={{ fontSize: '0.9rem', color: 'success.main' }} />
          : <FailedIcon sx={{ fontSize: '0.9rem', color: 'error.main' }} />}
        {totalSteps && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            [{stepNum}/{totalSteps}]
          </Typography>
        )}
        <MethodChip method={method} />
        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', flex: 1 }} noWrap>
          {url.replace(/^https?:\/\/[^/]+/, '')}
        </Typography>
        {statusCode && (
          <Chip label={statusCode} size="small"
            color={isSuccess ? 'success' : 'error'} variant="outlined"
            sx={{ height: 16, fontSize: '0.6rem' }} />
        )}
        {data.duration_ms != null && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            {data.duration_ms}ms
          </Typography>
        )}
        {category && (
          <Chip label={category} size="small" variant="outlined"
            sx={{ height: 16, fontSize: '0.6rem', textTransform: 'capitalize' }} />
        )}
        <IconButton size="small" sx={{ p: 0.15 }}>
          {expanded ? <ExpandLessIcon sx={{ fontSize: '0.85rem' }} /> : <ExpandMoreIcon sx={{ fontSize: '0.85rem' }} />}
        </IconButton>
      </Box>

      {/* Description + failure reason (always shown for failed tests) */}
      {!isSuccess && (
        <Box sx={{ px: 1, py: 0.5 }}>
          {description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem', mb: 0.25 }}>
              {description}
            </Typography>
          )}
          {group && (
            <Chip label={group} size="small" variant="outlined"
              sx={{ height: 16, fontSize: '0.6rem', mb: 0.5 }} />
          )}
          {statusCode && statusCodeReason(statusCode) && (
            <Alert severity="error" variant="outlined"
              sx={{ py: 0, px: 0.75, fontSize: '0.7rem', '& .MuiAlert-message': { py: 0.25 } }}>
              {statusCodeReason(statusCode)}
            </Alert>
          )}
          {error && !statusCodeReason(statusCode) && (
            <Alert severity="error" variant="outlined"
              sx={{ py: 0, px: 0.75, fontSize: '0.7rem', '& .MuiAlert-message': { py: 0.25 } }}>
              {typeof error === 'string' ? error : JSON.stringify(error)}
            </Alert>
          )}
          {responseBody && (
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="caption" fontWeight={600} fontSize="0.7rem" color="text.secondary">
                Response:
              </Typography>
              <Box sx={{
                mt: 0.25, p: 0.5, bgcolor: 'grey.50', borderRadius: 0.5,
                fontFamily: 'monospace', fontSize: '0.65rem', maxHeight: 80,
                overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
              }}>
                {typeof responseBody === 'string' ? responseBody : JSON.stringify(responseBody, null, 2)}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Expandable details for passed tests */}
      <Collapse in={expanded && isSuccess}>
        <Box sx={{ px: 1, py: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
          {description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem', mb: 0.25 }}>
              {description}
            </Typography>
          )}
          {group && (
            <Chip label={group} size="small" variant="outlined"
              sx={{ height: 16, fontSize: '0.6rem', mb: 0.5 }} />
          )}
          {requestBody && (
            <Box sx={{ mt: 0.25 }}>
              <Typography variant="caption" fontWeight={600} fontSize="0.7rem" color="text.secondary">
                Request Body:
              </Typography>
              <Box sx={{
                mt: 0.25, p: 0.5, bgcolor: 'grey.50', borderRadius: 0.5,
                fontFamily: 'monospace', fontSize: '0.65rem', maxHeight: 60,
                overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
              }}>
                {typeof requestBody === 'string' ? requestBody : JSON.stringify(requestBody, null, 2)}
              </Box>
            </Box>
          )}
          {responseBody && (
            <Box sx={{ mt: 0.25 }}>
              <Typography variant="caption" fontWeight={600} fontSize="0.7rem" color="text.secondary">
                Response Body:
              </Typography>
              <Box sx={{
                mt: 0.25, p: 0.5, bgcolor: 'grey.50', borderRadius: 0.5,
                fontFamily: 'monospace', fontSize: '0.65rem', maxHeight: 60,
                overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
              }}>
                {typeof responseBody === 'string' ? responseBody : JSON.stringify(responseBody, null, 2)}
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// ============================================================================
// Security Scan Card
// ============================================================================

const SecurityScanCard: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  const vulns: any[] = data.vulnerabilities || [];
  const hasCritical = vulns.some((v: any) => v.severity === 'critical' || v.severity === 'high');

  return (
    <Box sx={{
      border: '1px solid',
      borderColor: hasCritical ? 'error.200' : vulns.length > 0 ? 'warning.200' : 'success.200',
      borderRadius: 1.5,
      overflow: 'hidden',
      my: 0.5,
    }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: hasCritical ? 'error.50' : vulns.length > 0 ? 'warning.50' : 'success.50',
          cursor: vulns.length > 0 ? 'pointer' : 'default',
        }}
        onClick={() => vulns.length > 0 && setExpanded(!expanded)}
      >
        <SecurityIcon fontSize="small" color={hasCritical ? 'error' : vulns.length > 0 ? 'warning' : 'success'} />
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
          {vulns.length === 0
            ? 'Security scan: No vulnerabilities found'
            : `${vulns.length} vulnerabilit${vulns.length !== 1 ? 'ies' : 'y'} found`
          }
        </Typography>
        {data.risk_score != null && (
          <Chip label={`Risk: ${data.risk_score}`} size="small" color={hasCritical ? 'error' : 'warning'} sx={{ fontSize: '0.65rem', height: 20 }} />
        )}
        {vulns.length > 0 && (
          <IconButton size="small">
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        )}
      </Box>
      <Collapse in={expanded}>
        <List dense disablePadding sx={{ px: 1 }}>
          {vulns.map((v: any, idx: number) => (
            <ListItem key={idx} sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <Chip
                  label={v.severity}
                  size="small"
                  color={v.severity === 'critical' ? 'error' : v.severity === 'high' ? 'error' : v.severity === 'medium' ? 'warning' : 'default'}
                  sx={{ fontSize: '0.6rem', height: 18, fontWeight: 600, textTransform: 'uppercase' }}
                />
              </ListItemIcon>
              <ListItemText
                primary={v.type}
                secondary={v.evidence}
                primaryTypographyProps={{ variant: 'body2', fontSize: '0.85rem' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          ))}
        </List>
      </Collapse>
    </Box>
  );
};

// ============================================================================
// Strategist Insight Card
// ============================================================================

const SEVERITY_COLORS: Record<string, 'error' | 'warning' | 'info' | 'success' | 'default'> = {
  critical: 'error',
  high: 'error',
  medium: 'warning',
  low: 'info',
  info: 'default',
};

const ACTION_LABELS: Record<string, string> = {
  continue: 'Continuing...',
  skip_similar: 'Skipping similar endpoints',
  add_tests: 'Adding skill-generated tests',
  investigate: 'Investigating issue',
  stop: 'Stopping early',
};

const StrategistInsightCard: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  const findings: any[] = data.findings || [];
  const action = data.action || 'continue';
  const isActionable = action !== 'continue';

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: isActionable ? 'info.300' : 'grey.300',
        borderRadius: 1.5,
        p: 1.5,
        my: 0.5,
        bgcolor: isActionable ? 'info.50' : 'grey.50',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SmartToyIcon fontSize="small" color={isActionable ? 'info' : 'action'} />
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
          Strategist — Batch {data.batch_number || '?'}
        </Typography>
        <Chip
          label={ACTION_LABELS[action] || action}
          size="small"
          color={action === 'stop' ? 'error' : action === 'investigate' ? 'warning' : 'info'}
          variant="outlined"
          sx={{ fontSize: '0.7rem', height: 20, fontWeight: 600 }}
        />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {data.reasoning || ''}
      </Typography>

      {findings.length > 0 && (
        <>
          <Box
            sx={{ display: 'flex', alignItems: 'center', mt: 1, cursor: 'pointer' }}
            onClick={() => setExpanded(!expanded)}
          >
            <Typography variant="caption" fontWeight={600} sx={{ flex: 1 }}>
              {findings.length} finding{findings.length > 1 ? 's' : ''}
            </Typography>
            <IconButton size="small">
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Box>
          <Collapse in={expanded}>
            <List dense disablePadding sx={{ mt: 0.5 }}>
              {findings.map((f: any, i: number) => (
                <ListItem key={i} disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Chip
                      label={f.severity}
                      size="small"
                      color={SEVERITY_COLORS[f.severity] || 'default'}
                      sx={{ fontSize: '0.6rem', height: 18, fontWeight: 600, textTransform: 'uppercase' }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={f.title}
                    secondary={f.remediation || f.description}
                    primaryTypographyProps={{ variant: 'body2', fontSize: '0.85rem' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </>
      )}
    </Box>
  );
};

// ============================================================================
// Prior Results Loaded Card (Iterative Testing)
// ============================================================================

const PriorResultsLoadedCard: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  const steps: any[] = data.steps || [];
  const passedCount = data.passed_count || 0;
  const failedCount = data.failed_count || 0;
  const total = data.total_steps || steps.length;

  return (
    <Box sx={{ border: '1px solid', borderColor: 'info.200', borderRadius: 1.5, overflow: 'hidden', my: 0.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: 'info.50',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <RetryIcon fontSize="small" color="info" />
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
          Loaded {total} steps from previous session
        </Typography>
        <Chip label={`${passedCount} passed`} size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
        {failedCount > 0 && (
          <Chip label={`${failedCount} failed`} size="small" color="error" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
        )}
        <IconButton size="small">
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <List dense disablePadding sx={{ px: 1, maxHeight: 200, overflow: 'auto' }}>
          {steps.map((step: any, idx: number) => (
            <ListItem key={idx} sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                {step.status === 'passed' ? (
                  <PassedIcon fontSize="small" color="success" />
                ) : step.status === 'failed' ? (
                  <FailedIcon fontSize="small" color="error" />
                ) : (
                  <PendingIcon fontSize="small" color="disabled" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={step.description || step.action_id || `Step ${(step.step_index ?? idx) + 1}`}
                secondary={step.error || null}
                primaryTypographyProps={{ variant: 'body2', fontSize: '0.85rem' }}
                secondaryTypographyProps={{ variant: 'caption', color: 'error' }}
              />
            </ListItem>
          ))}
        </List>
      </Collapse>
      <Box sx={{ px: 1.5, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          Review the loaded steps, chat to make corrections, then approve to re-execute.
        </Typography>
      </Box>
    </Box>
  );
};

// ============================================================================
// Strategy Resolved Card
// ============================================================================

const STRATEGY_LABELS: Record<string, { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' }> = {
  smoke: { label: 'SMOKE', color: 'error' },
  sanity: { label: 'SANITY', color: 'warning' },
  regression: { label: 'REGRESSION', color: 'primary' },
  nightly: { label: 'NIGHTLY', color: 'info' },
  exploratory: { label: 'EXPLORATORY', color: 'default' },
};

const StrategyResolvedCard: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const strategy = data.strategy_type || 'exploratory';
  const info = STRATEGY_LABELS[strategy] || STRATEGY_LABELS.exploratory;

  return (
    <Box sx={{ border: '1px solid', borderColor: 'info.200', borderRadius: 1.5, p: 1.5, my: 0.5, bgcolor: 'info.50' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <ScienceIcon fontSize="small" color="info" />
        <Typography variant="body2" fontWeight={600}>Testing Strategy</Typography>
        <Chip label={info.label} size="small" color={info.color} sx={{ fontWeight: 700 }} />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="caption">
          Max pages: <strong>{data.max_pages}</strong>
        </Typography>
        <Typography variant="caption">
          Time budget: <strong>{data.time_budget_minutes} min</strong>
        </Typography>
        {data.abort_on_first_failure && (
          <Chip label="Abort on failure" size="small" color="error" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
        )}
        {data.skip_hitl_approval && (
          <Chip label="Auto-approve" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
        )}
        {data.critical_paths_only && (
          <Chip label="Critical paths only" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
        )}
        {!data.include_negative_tests && (
          <Chip label="Happy paths only" size="small" color="success" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
        )}
      </Box>
    </Box>
  );
};

// ============================================================================
// Strategy Abort Card
// ============================================================================

const StrategyAbortCard: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const strategy = data.strategy_type || 'unknown';
  const info = STRATEGY_LABELS[strategy] || { label: strategy.toUpperCase(), color: 'default' as const };

  return (
    <Box sx={{ border: '1px solid', borderColor: 'warning.300', borderRadius: 1.5, p: 1.5, my: 0.5, bgcolor: 'warning.50' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <StopIcon fontSize="small" color="warning" />
        <Typography variant="body2" fontWeight={600}>Testing Stopped Early</Typography>
        <Chip label={info.label} size="small" color={info.color} sx={{ fontWeight: 700 }} />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {data.reason}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="caption">
          Tests run: <strong>{data.tests_run}</strong>
        </Typography>
        <Typography variant="caption" color="success.main">
          Passed: <strong>{data.tests_passed}</strong>
        </Typography>
        {(data.tests_failed || 0) > 0 && (
          <Typography variant="caption" color="error.main">
            Failed: <strong>{data.tests_failed}</strong>
          </Typography>
        )}
        {data.elapsed_minutes != null && (
          <Typography variant="caption">
            Elapsed: <strong>{data.elapsed_minutes} min</strong>
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// ============================================================================
// Pipeline Started Card
// ============================================================================

const STAGE_LABELS: Record<string, string> = {
  app_analysis: 'App Analysis',
  test_strategy: 'Test Strategy',
  test_planning: 'Test Planning',
  test_authoring: 'Test Authoring',
  test_execution: 'Test Execution',
  bug_detection: 'Bug Detection',
  coverage_analysis: 'Coverage Analysis',
};

const PipelineStartedCard: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const config = data.config || {};
  return (
    <Box sx={{ border: '1px solid', borderColor: 'info.300', borderRadius: 1.5, p: 1.5, my: 0.5, bgcolor: 'info.50' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <ScienceIcon fontSize="small" color="info" />
        <Typography variant="body2" fontWeight={600}>QA Pipeline Started</Typography>
        <Chip
          label={(config.strategy_type || 'exploratory').toUpperCase()}
          size="small"
          color="primary"
          sx={{ fontWeight: 700 }}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="caption">
          URL: <strong>{config.url || '—'}</strong>
        </Typography>
        <Typography variant="caption">
          Stages: <strong>{data.total_stages || 7}</strong>
        </Typography>
        {config.time_budget_minutes && (
          <Typography variant="caption">
            Budget: <strong>{config.time_budget_minutes} min</strong>
          </Typography>
        )}
        {config.skip_execution && (
          <Chip label="Dry Run" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
        )}
      </Box>
      <LinearProgress variant="indeterminate" sx={{ mt: 1, borderRadius: 1 }} />
    </Box>
  );
};

// ============================================================================
// Pipeline Stage Card (started / complete / error)
// ============================================================================

const PipelineStageCard: React.FC<{ data: Record<string, any>; type: 'pipeline_stage_started' | 'pipeline_stage_complete' | 'pipeline_stage_error' }> = ({ data, type }) => {
  const stageName = STAGE_LABELS[data.stage] || data.stage;
  const stageNum = (data.stage_index ?? 0) + 1;
  const totalStages = data.total_stages || 7;
  const isStarted = type === 'pipeline_stage_started';
  const isError = type === 'pipeline_stage_error';
  const progressPct = Math.round((stageNum / totalStages) * 100);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
      {isStarted ? (
        <RunningIcon fontSize="small" color="primary" />
      ) : isError ? (
        <FailedIcon fontSize="small" color="error" />
      ) : (
        <PassedIcon fontSize="small" color="success" />
      )}
      <Typography variant="body2" fontSize="0.85rem" sx={{ flex: 1 }}>
        Stage {stageNum}/{totalStages}: {stageName}
      </Typography>
      {!isStarted && data.duration_ms != null && (
        <Typography variant="caption" color="text.secondary">
          {data.duration_ms}ms
        </Typography>
      )}
      {isError && data.error && (
        <Chip label="Error" size="small" color="error" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
      )}
      {!isError && !isStarted && (
        <Chip label={`${progressPct}%`} size="small" color="success" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
      )}
    </Box>
  );
};

// ============================================================================
// Pipeline Complete Card
// ============================================================================

const PipelineCompleteCard: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const isFailed = data.status === 'failed';
  const isAborted = data.status === 'aborted';
  const borderColor = isFailed ? 'error.200' : isAborted ? 'warning.200' : 'success.200';
  const bgColor = isFailed ? 'error.50' : isAborted ? 'warning.50' : 'success.50';
  const summary = data.result_summary || {};

  return (
    <Box sx={{ border: '1px solid', borderColor, borderRadius: 1.5, p: 1.5, my: 0.5, bgcolor: bgColor }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        {isFailed ? (
          <ErrorOutlineIcon fontSize="small" color="error" />
        ) : isAborted ? (
          <StopIcon fontSize="small" color="warning" />
        ) : (
          <CheckCircleOutlineIcon fontSize="small" color="success" />
        )}
        <Typography variant="body2" fontWeight={600}>
          Pipeline {isFailed ? 'Failed' : isAborted ? 'Aborted' : 'Complete'}
        </Typography>
        <Chip
          label={`${data.stages_completed || 0} stages`}
          size="small"
          color={isFailed ? 'error' : isAborted ? 'warning' : 'success'}
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      </Box>
      {data.total_duration_ms != null && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Total duration: {Math.round(data.total_duration_ms / 1000)}s
        </Typography>
      )}
      {data.error && (
        <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 0.5 }}>
          {data.error}
        </Typography>
      )}
      {data.reason && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Reason: {data.reason}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {summary.test_plan_id && (
          <Typography variant="caption">
            Test Plan: <strong>#{summary.test_plan_id}</strong>
          </Typography>
        )}
        {summary.test_cases_created != null && (
          <Typography variant="caption">
            Cases: <strong>{summary.test_cases_created}</strong>
          </Typography>
        )}
        {summary.test_run_id && (
          <Typography variant="caption">
            Test Run: <strong>#{summary.test_run_id}</strong>
          </Typography>
        )}
        {summary.pass_rate != null && (
          <Typography variant="caption" color={summary.pass_rate >= 80 ? 'success.main' : 'error.main'}>
            Pass Rate: <strong>{summary.pass_rate}%</strong>
          </Typography>
        )}
        {summary.bugs_found != null && summary.bugs_found > 0 && (
          <Typography variant="caption" color="error.main">
            Bugs: <strong>{summary.bugs_found}</strong>
          </Typography>
        )}
        {summary.coverage_grade && (
          <Typography variant="caption">
            Coverage: <strong>{summary.coverage_grade}</strong>
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// ============================================================================
// Main TestingEventCard switch component
// ============================================================================

interface TestingEventCardProps {
  card: TestingEventCardType;
  sessionId?: string;
}

const TestingEventCard: React.FC<TestingEventCardProps> = ({ card, sessionId }) => {
  switch (card.type) {
    case 'phase_change':
      return <PhaseChangeCard data={card.data} />;
    case 'steps_generated':
      return <StepsCard data={card.data} sessionId={sessionId} />;
    case 'step_executing':
    case 'step_completed':
      return <StepProgressCard data={card.data} type={card.type} />;
    case 'step_healed':
      return <HealingCard data={card.data} />;
    case 'approval_required':
      return <ApprovalCard data={card.data} sessionId={sessionId} />;
    case 'credential_request':
      return <CredentialRequestCard data={card.data} sessionId={sessionId} />;
    case 'login_status':
      return <LoginStatusCard data={card.data} />;
    case 'captcha_detected':
      return <CaptchaCard data={card.data} sessionId={sessionId} />;
    case 'review_failures':
      return <ReviewFailuresCard data={card.data} sessionId={sessionId} />;
    case 'quality_assessment':
      return <QualityAssessmentCard data={card.data} />;
    case 'code_generated':
      return <CodeGeneratedCard data={card.data} />;
    case 'session_complete':
      return <SessionCompleteCard data={card.data} />;
    case 'session_error':
      return <SessionErrorCard data={card.data} />;
    case 'otp_status':
      return <OtpStatusCard data={card.data} />;
    case 'dom_analysis':
      return <DomAnalysisCard data={card.data} />;
    case 'api_discovery':
      return <APIDiscoveryCard data={card.data} />;
    case 'api_config_request':
      return <APIConfigCard data={card.data} sessionId={sessionId} />;
    case 'api_batch_completed':
      return <APIBatchSummaryCard data={card.data} />;
    case 'api_analysis_complete':
      return <APIAnalysisCard data={card.data} />;
    case 'api_testing_knowledge':
      return <APITestingKnowledgeCard data={card.data} />;
    case 'api_step_result':
      return <APIStepResultCard data={card.data} />;
    case 'security_scan':
      return <SecurityScanCard data={card.data} />;
    case 'strategist_insight':
      return <StrategistInsightCard data={card.data} />;
    case 'page_selection_required':
      return (
        <Box sx={{ border: '1px solid', borderColor: 'info.200', borderRadius: 1.5, overflow: 'hidden', my: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, bgcolor: 'info.50' }}>
            <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
              {card.data.message || `Found ${(card.data.nodes || []).length} pages. Select which pages to test.`}
            </Typography>
            <Chip label="Select Pages" size="small" color="info" />
          </Box>
        </Box>
      );
    case 'prior_results_loaded':
      return <PriorResultsLoadedCard data={card.data} />;
    case 'strategy_resolved':
      return <StrategyResolvedCard data={card.data} />;
    case 'strategy_abort':
      return <StrategyAbortCard data={card.data} />;
    case 'pipeline_started':
      return <PipelineStartedCard data={card.data} />;
    case 'pipeline_stage_started':
    case 'pipeline_stage_complete':
    case 'pipeline_stage_error':
      return <PipelineStageCard data={card.data} type={card.type as 'pipeline_stage_started' | 'pipeline_stage_complete' | 'pipeline_stage_error'} />;
    case 'pipeline_complete':
      return <PipelineCompleteCard data={card.data} />;
    case 'page_testing_started':
      return (
        <Box sx={{ border: '1px solid', borderColor: 'primary.200', borderRadius: 1.5, overflow: 'hidden', my: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, bgcolor: 'primary.50' }}>
            <Typography variant="body2" fontWeight={600} color="primary.main">
              Page {(card.data.page_index ?? 0) + 1}/{card.data.total_pages || '?'}
            </Typography>
            <Typography variant="body2" sx={{ flex: 1 }}>
              {card.data.page_title || card.data.page_url}
            </Typography>
          </Box>
        </Box>
      );
    case 'test_cases_identified':
      return (
        <Box sx={{ border: '1px solid', borderColor: 'info.200', borderRadius: 1.5, overflow: 'hidden', my: 0.5 }}>
          <Box sx={{ px: 1.5, py: 0.75, bgcolor: 'info.50' }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              {(card.data.test_cases || []).length} test cases identified
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {(card.data.test_cases || []).slice(0, 5).map((tc: any, i: number) => (
                <Chip key={i} label={tc.name} size="small" variant="outlined"
                  color={tc.priority === 'high' ? 'error' : tc.priority === 'medium' ? 'warning' : 'default'}
                  sx={{ fontSize: '0.6rem', height: 20 }} />
              ))}
              {(card.data.test_cases || []).length > 5 && (
                <Chip label={`+${(card.data.test_cases || []).length - 5} more`} size="small" variant="outlined"
                  sx={{ fontSize: '0.6rem', height: 20 }} />
              )}
            </Box>
          </Box>
        </Box>
      );
    case 'page_code_generated':
      return (
        <Box sx={{ border: '1px solid', borderColor: 'success.200', borderRadius: 1.5, overflow: 'hidden', my: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, bgcolor: 'success.50' }}>
            <CodeIcon sx={{ fontSize: '1rem', color: 'success.main' }} />
            <Typography variant="body2" fontWeight={600}>
              {card.data.file_name || 'test.py'}
            </Typography>
            <Chip label={card.data.framework || 'playwright'} size="small" variant="outlined" color="success"
              sx={{ fontSize: '0.55rem', height: 18 }} />
          </Box>
        </Box>
      );
    case 'page_testing_completed': {
      const passed = card.data.passed ?? 0;
      const failed = card.data.failed ?? 0;
      const isPagePassed = failed === 0 && passed > 0;
      return (
        <Box sx={{
          border: '1px solid', borderRadius: 1.5, overflow: 'hidden', my: 0.5,
          borderColor: isPagePassed ? 'success.200' : 'error.200',
        }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75,
            bgcolor: isPagePassed ? 'success.50' : 'error.50',
          }}>
            {isPagePassed
              ? <PassedIcon sx={{ fontSize: '1rem', color: 'success.main' }} />
              : <FailedIcon sx={{ fontSize: '1rem', color: 'error.main' }} />
            }
            <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
              {card.data.page_title || 'Page'}
            </Typography>
            <Chip label={`${passed}P`} size="small" sx={{ height: 18, fontSize: '0.55rem', bgcolor: '#2e7d3220', color: '#2e7d32' }} />
            {failed > 0 && (
              <Chip label={`${failed}F`} size="small" sx={{ height: 18, fontSize: '0.55rem', bgcolor: '#c6282820', color: '#c62828' }} />
            )}
          </Box>
        </Box>
      );
    }
    case 'all_pages_completed': {
      const tp = card.data.total_passed ?? 0;
      const tf = card.data.total_failed ?? 0;
      return (
        <Box sx={{ border: '2px solid', borderColor: tf === 0 ? 'success.main' : 'warning.main', borderRadius: 1.5, overflow: 'hidden', my: 0.5 }}>
          <Box sx={{ px: 1.5, py: 1, bgcolor: tf === 0 ? 'success.50' : 'warning.50' }}>
            <Typography variant="body2" fontWeight={700}>
              All {card.data.total_pages || 0} pages tested
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {tp} passed, {tf} failed, {card.data.total_skipped ?? 0} skipped
            </Typography>
          </Box>
        </Box>
      );
    }
    default:
      return null;
  }
};

export default TestingEventCard;
